/**
 * Charts by Nex Flow Automation licensing contracts (M4).
 *
 * These types are shared by every layer that takes part in entitlement
 * resolution: the server (`services/api`, `services/worker`,
 * `@chartforge/licensing-core`), the client SDK (`@chartforge/license-sdk`) and
 * the host shells (Excel, Power BI, Sheets).
 *
 * Two rules are encoded here:
 *
 * 1. **The server is the source of truth.** A client never *computes* an
 *    entitlement; it only ever *verifies* one that the server signed.
 * 2. **Capabilities, not plans, are what gates a feature.** A plan is a
 *    marketing/billing concept; the engine and the UI check
 *    {@link FeatureCapability}. Plan→capability resolution lives on the server
 *    and is re-intersected client-side (see
 *    `engine-core/resolveCapabilities`) so a token can never widen access
 *    beyond its plan.
 */

/** Product plans, ordered by rank: `trial < basic < pro`. */
export type Tier = 'trial' | 'basic' | 'pro';

/** Billing interval of a purchasable price. */
export type BillingInterval = 'month' | 'year' | 'lifetime';

/** The five launch SKUs. */
export type SkuId =
  'basic_monthly' | 'basic_yearly' | 'pro_monthly' | 'pro_yearly' | 'pro_lifetime';

/**
 * Feature capabilities carried by an entitlement token.
 *
 * Keep this list short and behavioural — it is the vocabulary the engine, the
 * UI and every host use instead of `if (plan === 'pro')`.
 */
export const FEATURE_CAPABILITIES = [
  /** Render the timeline chart at all. */
  'timeline',
  /** Render the Gantt chart (M7). */
  'gantt',
  /** Export the chart (client-side SVG/PNG/PDF, and server-side SVG in M6). */
  'export',
  /** Remove the trial watermark (paid branding). */
  'branding',
  /** Layout/spacing/axis options beyond the trial set. */
  'advanced_layout',
  /** Data transformation options such as sorting categories by value. */
  'data_sorting',
  /**
   * Dependency arrows on the Gantt chart (M7).
   *
   * Deliberately separate from `gantt` and from `advanced_layout`: the timelines
   * (planned vs actual) are a paid presentation feature, so a plan can offer the
   * Gantt chart without offering dependency analysis.
   */
  'gantt_dependencies'
] as const;

export type FeatureCapability = (typeof FEATURE_CAPABILITIES)[number];

/** Runtime guard for untrusted (token/bridge/RPC) capability values. */
export function isFeatureCapability(value: unknown): value is FeatureCapability {
  return typeof value === 'string' && (FEATURE_CAPABILITIES as readonly string[]).includes(value);
}

/**
 * Capabilities a plan grants, before any narrowing.
 *
 * This table is the single source of truth for plan → capability mapping and is
 * shared by the server (which mints tokens from it) and by the client (which
 * re-intersects a token against it). A paid plan removes the trial watermark by
 * carrying `branding`; `pro` additionally unlocks `data_sorting` and the Gantt
 * dependency arrows.
 *
 * M7 note: `gantt` is granted to every plan including `trial`, because the M7
 * requirement is that a *new chart* works through the existing architecture. The
 * paid/free boundary in the Gantt chart is expressed where every other boundary
 * is — on individual `OptionSchema` entries (`gantt.dependencies` needs `pro` +
 * `gantt_dependencies`).
 */
export const PLAN_FEATURES: Readonly<Record<Tier, readonly FeatureCapability[]>> = Object.freeze({
  trial: Object.freeze(['timeline', 'gantt'] as FeatureCapability[]),
  basic: Object.freeze([
    'timeline',
    'gantt',
    'export',
    'advanced_layout',
    'branding'
  ] as FeatureCapability[]),
  pro: Object.freeze([
    'timeline',
    'gantt',
    'export',
    'advanced_layout',
    'branding',
    'data_sorting',
    'gantt_dependencies'
  ] as FeatureCapability[])
});

/** Capability list for a plan. Always returns a fresh array. */
export function baselineFeatures(plan: Tier): FeatureCapability[] {
  return [...(PLAN_FEATURES[plan] ?? PLAN_FEATURES.trial)];
}

/**
 * Narrow a declared capability list to what its plan allows.
 *
 * This is the client-side half of "the server is the source of truth": even if
 * a token (or a bug, or a future server version) declared `branding` on the
 * trial plan, the intersection drops it. Capabilities can therefore only ever
 * *reduce* access relative to the plan, never widen it.
 */
export function resolveFeatures(
  plan: Tier,
  declared?: readonly FeatureCapability[]
): FeatureCapability[] {
  const baseline = baselineFeatures(plan);
  if (!declared || declared.length === 0) {
    // A legacy `{ plan }` entitlement (M0–M3 shape) keeps the plan baseline.
    return baseline;
  }
  return baseline.filter((capability) => declared.includes(capability));
}

/** Effective capabilities of an entitlement value. Never throws. */
export function featuresOf(entitlements: {
  plan: Tier;
  features?: FeatureCapability[];
}): FeatureCapability[] {
  return resolveFeatures(entitlements.plan, entitlements.features);
}

/** True when an entitlement grants a capability. */
export function grantsFeature(
  entitlements: { plan: Tier; features?: FeatureCapability[] },
  capability: FeatureCapability
): boolean {
  return featuresOf(entitlements).includes(capability);
}

/** True when the plan's baseline includes a capability (server-side helper). */
export function planGrantsFeature(plan: Tier, capability: FeatureCapability): boolean {
  return (PLAN_FEATURES[plan] ?? PLAN_FEATURES.trial).includes(capability);
}

/* ------------------------------------------------------------------ */
/* License lifecycle                                                   */
/* ------------------------------------------------------------------ */

/**
 * Paddle's subscription status vocabulary mapped onto Charts by Nex Flow Automation's own states.
 * Kept deliberately identical to the subscription states we store, so no
 * translation table is needed when reading a row back.
 */
export type SubscriptionStatus = 'active' | 'trialing' | 'past_due' | 'canceled' | 'paused';

/** Entitlement status after resolution (server-computed, never client-guessed). */
export type LicenseStatus = SubscriptionStatus | 'expired' | 'none';

/** True when the status grants paid entitlements. */
export function isGrantingStatus(status: LicenseStatus | undefined): boolean {
  return status === 'active' || status === 'trialing' || status === 'past_due';
}

/* ------------------------------------------------------------------ */
/* Entitlements                                                        */
/* ------------------------------------------------------------------ */

/**
 * What a host is allowed to do right now.
 *
 * `plan` is always present so M0–M3 hosts keep working unchanged (it is the
 * only field the Sheets bridge required before M4). Everything else is
 * additive and optional: a host that renders `{ plan: 'trial' }` still gets
 * exactly the M3 behaviour.
 */
export interface Entitlements {
  plan: Tier;
  /**
   * Capabilities granted by the server. When absent the engine falls back to
   * the plan's baseline capability set, so a legacy `{ plan }` value behaves
   * exactly as it did before M4.
   */
  features?: FeatureCapability[];
  /** Lifecycle status the entitlement was derived from. */
  status?: LicenseStatus;
  /** Epoch ms when the signed entitlement itself stops being valid. */
  expiresAt?: number;
  /**
   * Epoch ms until which a *cached* entitlement may still be used offline
   * (Excel desktop's offline grace window). Absent means no grace is granted.
   */
  graceUntil?: number;
  /** True when the entitlement is bound to a specific device hash. */
  deviceBound?: boolean;
  /** Non-reversible hash of the license key (never the key itself). */
  licenseKeyHash?: string;
  /** Where the entitlement came from, for diagnostics and host provenance. */
  origin?: EntitlementOrigin;
}

/** Provenance of a resolved entitlement. */
export type EntitlementOrigin =
  /** Freshly verified against the licensing API. */
  | 'verified'
  /** Read from the host's own entitlement cache and still valid. */
  | 'cache'
  /** Read from cache past `exp` but inside the offline grace window. */
  | 'grace'
  /** No license: the device-scoped trial. */
  | 'trial'
  /** Verification failed (offline, revoked, malformed): degraded to trial. */
  | 'fallback'
  /** No licensing backend is configured at all (M0–M3 behaviour). */
  | 'stub';

/* ------------------------------------------------------------------ */
/* Signed entitlement token (JWT)                                      */
/* ------------------------------------------------------------------ */

/** `iss` claim value for every token issued by Charts by Nex Flow Automation. */
export const ENTITLEMENT_ISSUER = 'chartforge';

/** `aud` claim value for entitlement tokens. */
export const ENTITLEMENT_AUDIENCE = 'chartforge-host';

/**
 * Claims carried by the RS256-signed entitlement JWT.
 *
 * ```json
 * {
 *   "iss": "chartforge",
 *   "sub": "customer_123",
 *   "aud": "chartforge-host",
 *   "lic": "lk_abc",
 *   "plan": "pro",
 *   "feats": ["timeline", "export", "branding"],
 *   "device": "device-hash-xyz",
 *   "status": "active",
 *   "iat": 1699996399,
 *   "exp": 1699999999,
 *   "jti": "ent_01..."
 * }
 * ```
 *
 * `exp` is deliberately short (see `ENTITLEMENT_TTL_SECONDS`); hosts extend
 * their *offline* window from the cached copy under an explicit grace policy
 * rather than from a long-lived token.
 */
export interface EntitlementClaims {
  /** Issuer — always {@link ENTITLEMENT_ISSUER}. */
  iss: string;
  /** Subject: the Charts by Nex Flow Automation customer id (`cust_...`). Never an email. */
  sub: string;
  /** Audience — always {@link ENTITLEMENT_AUDIENCE}. */
  aud: string;
  /** License key id (`lk_...`), the durable identifier of the license. */
  lic: string;
  /**
   * Non-reversible hash of the raw license key the token was issued for. Lets a
   * host key its cache exactly as the server does without ever handling the
   * plaintext key again. Absent for device-scoped trials.
   */
  lkh?: string;
  /** Plan tier. */
  plan: Tier;
  /** Capability list (see {@link FeatureCapability}). */
  feats: FeatureCapability[];
  /** Device hash this entitlement is bound to, when device-bound. */
  device?: string;
  /** Subscription/entitlement status. */
  status?: LicenseStatus;
  /**
   * Server-side cap on the *offline grace* window, in seconds. A host policy
   * longer than this is clamped: the server remains the source of truth for how
   * long an offline client may keep working.
   */
  grs?: number;
  /** Issued at (seconds since epoch). */
  iat: number;
  /** Expiry (seconds since epoch). */
  exp: number;
  /** Unique token id, so a refresh can be traced (and rate-limited). */
  jti: string;
}

/* ------------------------------------------------------------------ */
/* API request/response contracts                                      */
/* ------------------------------------------------------------------ */

/** Envelope returned by every licensing endpoint. */
export interface EntitlementResponse {
  /** Compact RS256 JWT — the only thing a host may act on. */
  token: string;
  /** Epoch ms when `token` expires (duplicated for host cache TTLs). */
  expiresAt: number;
  /** Epoch ms when the host should proactively refresh. */
  refreshAt: number;
  /** Entitlements decoded from the token, for host convenience. */
  entitlements: Entitlements;
  /** `kid` of the signing key, so a cached JWKS can be matched. */
  kid: string;
  /** True when the response came from the entitlement cache. */
  cached: boolean;
}

/** `POST /v1/licenses/activate` */
export interface ActivationRequest {
  licenseKey: string;
  deviceHash?: string;
  /** Human-readable device label, e.g. `Excel 16.0 (Windows)`. Minimised data. */
  deviceLabel?: string;
  clientVersion?: string;
}

/** `POST /v1/licenses/activate` and `POST /v1/licenses/validate` */
export interface ActivationResponse extends EntitlementResponse {
  licenseKeyId: string;
  /** Activations used / allowed, for device-bound one-time licenses. */
  activationsUsed: number;
  activationLimit: number;
}

/** `POST /v1/licenses/deactivate` */
export interface DeactivationResponse {
  ok: boolean;
  activationsUsed: number;
  activationLimit: number;
}

/**
 * Hosts that can originate a licence request.
 *
 * Superset of {@link PlatformId}: `portal` and `unknown` are not platforms. The
 * API stores this verbatim on licence audit rows and echoes it into Paddle
 * `custom_data`, so an unrecognised value is coerced to `unknown` rather than
 * rejected — telemetry must never fail a purchase.
 */
export type LicenseRequestHost = 'excel' | 'sheets' | 'powerbi' | 'web' | 'portal' | 'unknown';

/** `POST /v1/trials` — cardless, device-scoped. */
export interface TrialRequest {
  deviceHash: string;
  deviceLabel?: string;
  /** Host the trial was started from, for analytics only. */
  host?: LicenseRequestHost;
  clientVersion?: string;
}

/** `POST /v1/trials` */
export interface TrialResponse extends EntitlementResponse {
  trialId: string;
  /** Epoch ms when the trial ends. */
  trialEndsAt: number;
}

/** `POST /v1/checkout/sessions` */
export interface CheckoutSessionRequest {
  sku: SkuId;
  /** Device hash, so the resulting license can be device-bound. */
  deviceHash?: string;
  /** Host that opened the checkout, echoed into Paddle `custom_data`. */
  host?: LicenseRequestHost;
  /** Existing customer to reuse, when the host already has one. */
  customerId?: string;
}

/** `POST /v1/checkout/sessions` */
export interface CheckoutSessionResponse {
  transactionId: string;
  /** Paddle checkout URL (`checkout.url`), when Paddle returned one. */
  checkoutUrl: string | null;
  sku: SkuId;
  /** Environment the session was created in — never mixed across sandboxes. */
  environment: ChartForgeEnvironment;
}

/** Deployment environments. Production billing is *not* switched in M4. */
export type ChartForgeEnvironment = 'development' | 'staging' | 'production';

/** Machine-readable error body (never contains a secret or a raw key). */
export interface LicenseErrorBody {
  error: {
    code: string;
    message: string;
    /** True when a retry after user action could succeed. */
    recoverable?: boolean;
    /** Correlates a client report with a server log line. */
    requestId?: string;
  };
}
